import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9p0jlaftmtnfcfh0fe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Header */}
        <section className="bg-background-100/70 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4">
              关于布丁漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2 max-w-2xl">
              免费阅读的在线韩国漫画平台，最新最全的韩漫官网，致力于为漫画爱好者提供优质的阅读体验
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
              <div className="lg:w-1/2">
                <div className="rounded-2xl overflow-hidden bg-background-200 aspect-[4/3] w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20space%20with%20bookshelves%20and%20comfortable%20seating%2C%20soft%20natural%20light%20streaming%20through%20windows%2C%20artistic%20Korean%20style%20illustration%2C%20warm%20coral%20and%20cream%20color%20palette%2C%20peaceful%20atmosphere%2C%20digital%20art%20with%20gentle%20textures%2C%20elegant%20composition&width=900&height=675&seq=pudding-about-brand&orientation=landscape"
                    alt="布丁漫画品牌形象 - 温暖舒适的阅读空间"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="lg:w-1/2">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  布丁漫画成立于2023年，由一群热爱韩国漫画的资深漫迷共同创立。我们深知漫迷们对高质量韩漫内容的渴望，因此致力于打造一个真正为读者服务的漫画平台——<strong>完全免费、更新迅速、内容丰富</strong>。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们与多家韩国官方漫画平台建立了深度合作关系，确保所有漫画内容均来自官方授权渠道。这意味着，布丁漫画的读者可以在第一时间阅读到最新的漫画章节，同时享受到官方级别的翻译质量和画质体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  截至目前，布丁漫画已收录超过数百部精品韩国漫画，涵盖恋爱、奇幻、动作、悬疑、喜剧、都市、校园、穿越等全品类，累计服务超过百万漫画爱好者。未来，我们将继续扩展内容库，为读者带来更多优质的韩漫作品。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="bg-background-100/70 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              核心价值
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-heart-pulse-line',
                  title: '热爱驱动',
                  desc: '我们因为对漫画的热爱而走到一起。每一部作品的选择、每一次平台的优化，都源于对漫画的真诚热爱和对读者的责任感。',
                },
                {
                  icon: 'ri-verified-badge-line',
                  title: '品质至上',
                  desc: '我们坚持与官方合作，拒绝盗版侵权内容。所有漫画均获得官方授权，保证画面质量与更新速度，给读者最好的阅读体验。',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '用户为本',
                  desc: '读者永远是我们最关心的。我们持续优化平台体验，提供多端适配、流畅阅读、个性化推荐等功能，让阅读成为一种享受。',
                },
                {
                  icon: 'ri-rocket-2-line',
                  title: '极速更新',
                  desc: '与官方同步更新是我们的承诺。我们的编辑团队全天候待命，确保韩国官方发布新章节后，第一时间完成翻译与上线。',
                },
                {
                  icon: 'ri-global-line',
                  title: '开放共享',
                  desc: '我们相信优质的漫画内容应该被更多人看到。布丁漫画坚持免费模式，让每一位漫画爱好者都能轻松享受阅读的乐趣。',
                },
                {
                  icon: 'ri-shield-star-line',
                  title: '安全可靠',
                  desc: '平台采用先进的技术架构，确保用户数据安全与阅读隐私。我们严格遵守相关法律法规，为用户提供放心的阅读环境。',
                },
              ].map((value, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl mb-3">
                    <i className={`${value.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              口碑与数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
              {[
                { value: '100万+', label: '月活跃用户' },
                { value: '500+', label: '收录漫画作品' },
                { value: '99.7%', label: '用户好评率' },
                { value: '24h', label: '平均更新速度' },
              ].map((stat, i) => (
                <div key={i} className="bg-background-100/70 rounded-xl p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="bg-background-100/70 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              用户评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  name: '小王',
                  role: '资深漫迷',
                  comment: '布丁漫画是我用过最好的韩漫平台，更新速度真的很快，官方源的质量也没话说。推荐给所有喜欢韩漫的朋友！',
                  rating: 5,
                },
                {
                  name: 'Lisa Chen',
                  role: '大学生',
                  comment: '完全免费的漫画平台，画质清晰，分类也很全。我特别喜欢恋爱和校园类的漫画，每天都要刷刷更新。',
                  rating: 5,
                },
                {
                  name: '阿杰',
                  role: '程序员',
                  comment: '作为一个技术人，我很欣赏布丁漫画的页面设计，简洁好用不卡顿。漫画资源也非常丰富，强烈推荐。',
                  rating: 5,
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }, (_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">
                        {review.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '布丁漫画真的完全免费吗？',
                  a: '是的，布丁漫画所有漫画内容均免费提供阅读，无需付费，无需会员。我们通过广告和品牌合作维持运营，不会向读者收费。',
                },
                {
                  q: '漫画多久更新一次？',
                  a: '我们与韩国官方同步更新，大部分热门漫画每周更新1-2话。您可以在每部漫画的详情页查看具体的更新频率和时间。',
                },
                {
                  q: '漫画内容是否为正版？',
                  a: '布丁漫画所有内容均来自官方授权渠道，我们坚决支持正版漫画，反对任何形式的盗版侵权行为。',
                },
                {
                  q: '支持哪些设备阅读？',
                  a: '布丁漫画支持电脑、平板、手机等多终端阅读，页面会自动适配不同屏幕尺寸，给您最佳的阅读体验。',
                },
                {
                  q: '如何反馈问题或提出建议？',
                  a: '您可以通过本页下方的联系表单给我们留言，或发送邮件至 contact@budingmanhua.com，我们会尽快回复。',
                },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-100/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer">
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.q}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="bg-background-100/70 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议？欢迎给我们留言，我们会尽快回复
            </p>

            {formStatus === 'success' ? (
              <div className="bg-background-50 rounded-xl p-8 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 rounded-2xl mb-4">
                  <i className="ri-check-line text-primary-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  提交成功！
                </h3>
                <p className="text-sm text-foreground-500">
                  感谢您的留言，我们会尽快回复您。
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-4 px-4 py-2 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送新留言
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-xl p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-website-alt"
                />

                {formStatus === 'error' && (
                  <div className="mb-4 p-3 bg-secondary-100/50 border border-secondary-200 rounded-lg">
                    <p className="text-sm text-secondary-700">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于布丁漫画 - 免费在线韩国漫画平台',
            description: '布丁漫画是最新最全的韩漫官网，提供免费在线韩国漫画阅读，官方源更新保证速度与质量。',
            url: 'https://budingmanhua.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '布丁漫画',
              url: 'https://budingmanhua.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '布丁漫画真的完全免费吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，布丁漫画所有漫画内容均免费提供阅读，无需付费，无需会员。我们通过广告和品牌合作维持运营，不会向读者收费。',
                },
              },
              {
                '@type': 'Question',
                name: '漫画多久更新一次？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '我们与韩国官方同步更新，大部分热门漫画每周更新1-2话。您可以在每部漫画的详情页查看具体的更新频率和时间。',
                },
              },
              {
                '@type': 'Question',
                name: '漫画内容是否为正版？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '布丁漫画所有内容均来自官方授权渠道，我们坚决支持正版漫画，反对任何形式的盗版侵权行为。',
                },
              },
              {
                '@type': 'Question',
                name: '支持哪些设备阅读？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '布丁漫画支持电脑、平板、手机等多终端阅读，页面会自动适配不同屏幕尺寸，给您最佳的阅读体验。',
                },
              },
              {
                '@type': 'Question',
                name: '如何反馈问题或提出建议？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过本页下方的联系表单给我们留言，或发送邮件至 contact@budingmanhua.com，我们会尽快回复。',
                },
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://budingmanhua.com' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />

      <Footer />
    </>
  );
}